import { type Ref, ref } from 'vue';

import { useDialogStore } from '@/stores/useDialogStore';
import { useJobEditStore } from '@/stores/useJobEditStore';
import { showWarningNotification } from '@/helpers/notifiers';
import {
  KJobEditError,
  KJobEditUpdateLockedError,
  KJobEditUpdateNotFoundError,
  KJobEditUpdateUnlockedError,
} from '@/helpers/throwables';

export interface JobEditSectionConfig {
  sectionName: string;
  saveOperation: () => Promise<boolean>;
  onCancel?: () => void | Promise<void>;
}

export interface JobEditComposableBase {
  startEditing: () => Promise<void>;
  cancelEditing: () => Promise<void>;
  saveEditing: () => Promise<boolean>;
}

export interface UseJobEditSectionBaseReturn extends JobEditComposableBase {
  isEditing: Ref<boolean>;
  isLoading: Ref<boolean>;
}

export function useJobEditSectionBase(
  config: JobEditSectionConfig,
): UseJobEditSectionBaseReturn {
  const jobEditStore = useJobEditStore();
  const dialogStore = useDialogStore();

  const isEditing = ref(false);
  const isLoading = ref(false);

  async function startEditing(): Promise<void> {
    if (isEditing.value) {
      return;
    }

    if (
      jobEditStore.lockState.isLocked &&
      jobEditStore.lockState.currentSection !== null &&
      jobEditStore.lockState.currentSection !== config.sectionName
    ) {
      throw new KJobEditError('Another section is already being edited');
    }

    try {
      isLoading.value = true;

      jobEditStore.setCurrentSection(config.sectionName);

      if (!jobEditStore.lockState.isLocked) {
        await jobEditStore.lockJob();
      }

      isEditing.value = true;
    } catch (error) {
      isEditing.value = false;
      jobEditStore.setCurrentSection(null);
      throw error instanceof KJobEditError
        ? error
        : new KJobEditError('Failed to start editing');
    } finally {
      isLoading.value = false;
    }
  }

  async function cancelEditing(): Promise<void> {
    if (!isEditing.value) {
      return;
    }

    try {
      isLoading.value = true;

      await jobEditStore.unlockJob();
      await config.onCancel?.();

      isEditing.value = false;
    } catch (error) {
      throw error instanceof KJobEditError
        ? error
        : new KJobEditError('Failed to cancel editing');
    } finally {
      isLoading.value = false;
    }
  }

  function resetEditingState(): void {
    isEditing.value = false;
    jobEditStore.setCurrentSection(null);
  }

  async function relockAndRetrySave(): Promise<boolean> {
    // Lock expired on the backend; reset local state and re-acquire.
    jobEditStore.resetLock();
    try {
      await jobEditStore.lockJob();
    } catch {
      // lockJob already opened the appropriate dialog (LockedByOther / NotFound).
      // Clear the editing state so the overlay doesn't stay stuck after failure.
      resetEditingState();
      return false;
    }
    try {
      return await config.saveOperation();
    } catch (error) {
      if (error instanceof KJobEditUpdateUnlockedError) {
        // Lock keeps expiring; show a dedicated message — retrying would loop.
        dialogStore.open('jobEditError', {
          errorMessage:
            'Job lock keeps expiring. Please reload the page and try again.',
          isConfirmLoading: false,
          retryCallback: async () => {
            await cancelEditing();
          },
          cancelCallback: async () => {
            await cancelEditing();
          },
        });
        resetEditingState();
        return false;
      }
      throw error;
    }
  }

  async function performSave(): Promise<boolean> {
    try {
      return await config.saveOperation();
    } catch (error) {
      if (error instanceof KJobEditUpdateUnlockedError) {
        return await relockAndRetrySave();
      }
      throw error;
    }
  }

  async function saveEditing(): Promise<boolean> {
    if (!isEditing.value) {
      return true;
    }

    try {
      isLoading.value = true;

      const saveResult = await performSave();

      if (!saveResult) {
        return false;
      }

      try {
        await jobEditStore.loadJob(true);
      } catch {
        // TODO: Potential dangerous place.
        //   consider adding a retry mechanism or at least a more visible notification to the user about the failure
        showWarningNotification({
          title:
            'Changes saved, but data could not be refreshed. Please reload the job to see updated data',
        });
      }

      await jobEditStore.unlockJob();

      isEditing.value = false;
      void jobEditStore.notifyDataChanged();

      return saveResult;
    } catch (error) {
      // Centralized error handling with dialog opening
      if (error instanceof KJobEditUpdateLockedError) {
        dialogStore.open('jobEditLockedByOther', {
          lockedBy: error.lockedBy || 'another user',
        });
      } else if (error instanceof KJobEditUpdateNotFoundError) {
        dialogStore.open('jobEditNotFound', {
          errorMessage: 'Job not found',
        });
      } else if (error instanceof Error) {
        dialogStore.open('jobEditError', {
          errorMessage:
            error.message || `Failed to save section ${config.sectionName}`,
          isConfirmLoading: false,
          retryCallback: async () => {
            await saveEditing();
          },
          cancelCallback: async () => {
            await cancelEditing();
          },
        });
      }

      throw error instanceof KJobEditError
        ? error
        : new KJobEditError('Failed to save');
    } finally {
      isLoading.value = false;
    }
  }

  return {
    startEditing,
    cancelEditing,
    saveEditing,
    isEditing,
    isLoading,
  };
}
